import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCreateDirective, useDirectives, useRevokeDirective } from "~/api/queries";
import { Button } from "~/foundation/components/Button";
import { Input } from "~/foundation/components/Input";
import { Spinner } from "~/foundation/components/Spinner";

/**
 * Forward-only steering: a short instruction the executor picks up at the next node boundary.
 *
 * Two things this deliberately does not do. It does not edit a paused node's already-built
 * prompt — that prompt was assembled and sent, and pretending it can be rewritten in place is
 * a lie about what the executor is holding. And it does not reach into a run: directives are
 * addressed to the *flow*, so every run of that flow reads them.
 */
export function SteeringPanel({ flowId }: { flowId: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const { data: directives, isLoading } = useDirectives(flowId);
  const create = useCreateDirective();
  const revoke = useRevokeDirective();

  const submit = () => {
    const value = text.trim();
    if (!value) {
      return;
    }
    create.mutate(
      { flowId, text: value },
      {
        onSuccess: () => {
          setText("");
          toast.success(t("Directive added — it reaches the executor at the next node boundary."));
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-divider bg-bg-secondary px-3 py-2.5">
      <p className="text-small text-text-secondary">
        {t(
          "Directives reach the executor at the next node boundary once extension 0.5.52 or later is live; flows can also read them through the `directives` source.",
        )}
      </p>
      <p className="text-small text-text-tertiary">
        {t(
          "Editing a paused node's already-built prompt is not supported — steering is forward-only. Use Jump to move the cursor instead.",
        )}
      </p>

      <div className="flex items-center gap-2">
        <Input
          value={text}
          placeholder={t("Tell the next node something")}
          aria-label={t("New directive")}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }
          }}
        />
        <Button size="sm" onClick={submit} disabled={create.isPending || text.trim().length === 0}>
          {t("Add")}
        </Button>
      </div>

      {isLoading ? <Spinner size={14} /> : null}
      {directives && directives.length === 0 ? (
        <span className="text-small text-text-tertiary">
          {t("No active directives for this flow.")}
        </span>
      ) : null}
      <ul className="flex flex-col gap-1">
        {(directives ?? []).map((directive) => (
          <li key={directive.id} className="flex items-center gap-2 text-body text-text">
            <span className="min-w-0 flex-1 truncate">{directive.text}</span>
            <span className="shrink-0 text-small text-text-tertiary">
              {directive.createdBy ?? t("unknown")}
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={revoke.isPending}
              onClick={() => revoke.mutate(directive.id)}
            >
              {t("Revoke")}
            </Button>
          </li>
        ))}
      </ul>
    </div>
  );
}
